<template>
  <GenericSideMenu
    position="right"
    class="MiniCart overflow-y-auto"
    content-class="!p-0"
    :title="`${$t('minicart.headline')}`"
    :title-meta="cartItems"
    :title-description="titleDescription"
    :with-extension="!uiStore.isMobile && relatedProducts.length > 0"
    @close="close"
  >
    <template v-if="globalContent.getIsB2b" #extended>
      <div class="w-full">
        <div
          class="type-headline-lg p-12"
        >
          {{ $t('accessories.headline') }}
        </div>
        <client-only>
          <div
            v-for="(product, i) in relatedProducts"
            :key="product.partNo"
            class="px-12 pb-24"
          >
            <ProductCard
              :product="product"
              :enable-quick-buy="true"
              :placement="i"
              :design="'miniCart'"
            />
          </div>
        </client-only>
      </div>
    </template>

    <CheckoutFreeShippingProgressBar
      v-if="cartStore.items.length && ((globalContent.getIsB2b && globalContent.getEnableFreeShippingProgress && !globalContent.getIsB2bSampleCheckout) || (!globalContent.getIsB2b && !cartStore.hasOnlySamples && ((globalContent.getCountryCode === 'gb' && globalContent.getEnableFreeShippingProgress) || globalContent.getCountryCode === 'se')))"
      class="px-12 pt-12 desk:px-24"
    />

    <LoadBasketById
      v-if="(userStore.isCustomerSuccessUser || userStore.isSalesRepUser) && !isOrderProposalBasket && !copiedFromOrderId"
      class="px-12 desk:px-24 pt-8"
    />

    <CopiedFromOrderBanner
      v-if="copiedFromOrderId"
      :order-id="copiedFromOrderId"
      class="px-12 desk:px-24 pt-12 desk:pt-24"
    />

    <EditingOrderProposalBanner
      v-if="isOrderProposalBasket"
      :order-proposal-id="orderProposalId"
      @clear-sales="clearOrderProposalForSalesRep"
      @clear-customer="clearOrderProposalForCustomer"
    />

    <div
      v-if="showSavedCartMessage"
      class="w-full p-12 desk:p-24 desk:pb-12 type-xs font-medium"
    >
      <p class="bg-lighter p-12">
        {{ $t('minicart.savedCart.messsage', {cartId:cartStore.cart.norceBasketId, date: getLocaleDate(cartStore.cart.savedBasket?.createdAt, { year: 'numeric', month: 'numeric', day: 'numeric' }), name: cartStore.cart.savedBasket?.creatorName ?? 'N/A'}) }}
      </p>
    </div>

    <div
      v-if="cartStore.items.length"
      class="p-12 desk:p-24"
      :class="{
        'pt-24': orderProposalId,
      }"
    >
      <CrossdockingBanner v-if="isBackorderBasket && globalContent.getAllowBackorders" />

      <!-- <button
        v-if="globalContent.getFastOrderEnabled && userStore.isSalesRepUser"
        class="btn btn&#45;&#45;secondary w-full mt-12"
        @click="uiStore.setShowScanner(true)"
       type="button">
        <img
          alt=""
          src="/icons/barcode-read.svg"
          class="inline-block w-16 h-16 mr-12"
        >
        {{ $t('minicart.barcode.scan') }}
      </button>-->

      <MiniCartProductV2
        v-for="(item) in visibleCartItems"
        :key="item.lineNo"
        :item="item"
        :hide-cross-docking="!globalContent.getAllowBackorders || userStore.isSalesRepUser || userStore.isCustomerSuccessUser || cartStore.items.length < 2"
        :disable-interaction="item.partNo === globalContent.palletDiscountPartNo"
      />
      <div
        class="flex flex-col gap-12 desk:gap-16 py-12 desk:pb-16 desk:pt-12 type-xs desk:type-sm leading-single"
        :class="{
          'opacity-50': cartStore.isLoading
        }"
      >
        <div v-if="showPoints && isCheckout" class="flex justify-between">
          <div class="flex items-center">
            <div>
              {{ $t('minicart.summary.points') }}
            </div>
            <Tooltip class="ml-6" :text="$t('minicart.summary.pointsInfo', {amount: globalContent.getPointCollectingThreshold})">
              <img
                class="w-12 h-12 desk:w-16 desk:h-16"
                src="/icons/info.svg"
                alt=""
              >
            </Tooltip>
          </div>
          <div class="pl-16 font-medium text-right">
            {{ `${cartStore.cart.summary.points} ${$t('minicart.summary.pointsSuffix')}` }}
          </div>
        </div>
        <div v-if="showPoints && isCheckout" class="color-light h-1 w-full" />
        <div
          v-for="fee in cartStore.cart.summary?.additionalFees"
          :key="fee.name"
          class="flex justify-between"
        >
          <div>{{ fee.name }}</div>
          <div class="pl-16 font-medium text-right">
            {{ fee.feeBeforeVatDisplay }}
          </div>
        </div>
        <div v-if="isCheckout" class="flex justify-between">
          <div>{{ $t('minicart.summary.sum') }}</div>
          <div class="pl-16 font-medium text-right">
            {{ cartStore.cart.summary?.subtotalBeforeVatDisplay }}
          </div>
        </div>
        <div v-if="isCheckout" class="flex justify-between">
          <div>{{ $t('minicart.summary.freight') }}</div>
          <div class="pl-16 font-medium text-right">
            <span>{{ (globalContent.getIsB2bSampleCheckout || cartStore.cart.summary?.shippingBeforeVat === '0')
              ? $t('checkout.shipping.free')
              : cartStore.cart.summary?.shippingBeforeVatDisplay }}</span>
          </div>
        </div>
        <div
          v-for="extraFee in deliveryExtraFeeItems"
          :key="extraFee.lineNo"
          class="flex justify-between"
        >
          <div>{{ extraFee.variantName || extraFee.name }}</div>
          <div class="pl-16 font-medium text-right">
            {{ extraFee.priceInfoTotal?.priceBeforeVatDisplay || extraFee.priceInfo?.priceBeforeVatDisplay }}
          </div>
        </div>
        <div v-if="cartStore.cart.summary?.crossDockingShippingBeforeVat && isCheckout" class="flex justify-between">
          <div>{{ $t('crossDocking.shipping') }}</div>
          <div class="pl-16 text-right">
            <span>{{ cartStore.cart.summary?.crossDockingShippingBeforeVatDisplay }}</span>
          </div>
        </div>
        <div v-if="cartStore.cart.summary.vatDisplay && isCheckout" class="flex justify-between">
          <div>{{ $t('minicart.summary.vat') }}</div>
          <div class="pl-16 font-medium text-right">
            ({{ cartStore.cart.summary.vatDisplay.trim() }})
          </div>
        </div>
        <div
          v-if="cartStore.hasActualDiscount"
          class="flex justify-between items-center"
        >
          <div>{{ $t('minicart.summary.discount') }}</div>
          <div
            class="pl-16 text-right text-criticalDark"
            :class="{
              'type-headline-sm text-text-errorStrong': !isCheckout
            }"
          >
            -{{ cartStore.cart.summary.discountDisplay.trim() }}
          </div>
        </div>
        <div v-if="isCheckout || (cartStore.hasActualDiscount && !isCheckout)" class="color-light h-1 w-full" />
        <div>
          <div class="flex justify-between items-end mb-8 type-sm leading-single">
            <div>{{ globalContent.getDisplayIncVat ? $t('price.incVat.short') : $t('minicart.summary.exVat') }}</div>
            <div class="flex items-center gap-8">
              <div
                v-if="!hideFreightInFlyoutTotal && cartStore.cart.summary.priceBeforeVatAndDiscount && cartStore.cart.summary.priceBeforeVatAndDiscount !== cartStore.cart.summary.priceBeforeVat && cartStore.hasActualDiscount"
                class="type-base text-text-subtle line-through"
              >
                {{ globalContent.getDisplayIncVat ? cartStore.cart.summary.priceBeforeDiscountDisplay : cartStore.cart.summary.priceBeforeVatAndDiscountDisplay }}
              </div>
              <div
                class="leading-single type-headline-base text-text-subtle"
              >
                {{ globalContent.getDisplayIncVat ? displayTotal : displayTotalBeforeVat }}
              </div>
            </div>
          </div>
          <div v-if="!globalContent.getDisplayIncVat" class="flex justify-between items-end text-text-subtle">
            <div class="capitalize">({{ $t('price.incVat.short') }})</div>
            <div class="pl-16 text-right">({{ displayTotal.trim() }})</div>
          </div>
          <div v-else class="flex justify-between items-end text-text-subtle">
            <div class="capitalize">({{ $t('minicart.summary.exVat') }})</div>
            <div class="pl-16 text-right">({{ displayTotalBeforeVat.trim() }})</div>
          </div>
        </div>
        <nuxt-link
          :to="manualUrl('/checkout')"
          class="btn btn--lg w-full"
          :class="{
            'loading': cartStore.isLoading
          }"
        >
          {{ $t('minicart.toCheckout') }}
        </nuxt-link>
        <button
          v-if="cartStore.shouldShowOrderProposalRequest && globalContent.getEnableOrderProposalRequest && !userStore.isSalesRepUser"
          class="btn btn--lg btn--secondary w-full"
          type="button"
          @click="showRequestProposal = true"
        >
          {{ $t('minicart.requestOrderProposal') }}
        </button>

        <div v-if="globalContent.getIsB2b" class="flex items-center justify-center gap-6 type-sm">
          <img
            src="/icons/truck.svg"
            class="text-darkest w-20 h-20"
            alt="delivery"
          >
          {{ $t('miniCart.shipping.calculatedInCheckout') }}
        </div>
      </div>

      <div v-if="globalContent.getIsB2b" class="desk:hidden">
        <div v-if="relatedProducts.length" class="type-headline-lg leading-single flex items-start my-12">
          {{ $t('accessories.headline') }}
        </div>
        <client-only>
          <swiper-container
            :key="`swiperForTab${swiperIndex}`"
            :space-between="12"
            :slides-per-view="2.2"
            :slides-offset-before="0"
            :slides-offset-after="24"
            :navigation="true"
            :pagination="{
              dynamicBullets: true,
              clickable: true,
            }"
            :a11y-prev-slide-message="$t('wcag.arialabel.previousSlide')"
            :a11y-next-slide-message="$t('wcag.arialabel.nextSlide')"
            :a11y-pagination-bullet-message="$t('wcag.arialabel.goToSlide')"
            @resize="swiper => { updateSwiperH(swiper.height) }"
          >
            <template
              v-for="(product, i) in relatedProducts"
              :key="product.partNo"
            >
              <swiper-slide>
                <ProductCard
                  :product="product"
                  :enable-quick-buy="true"
                  :placement="i"
                  :design="'miniCart'"
                />
              </swiper-slide>
            </template>
          </swiper-container>
        </client-only>
      </div>
    </div>
    <div v-else class="flex flex-col items-center justify-center p-24 h-full w-full relative pb-88 type-sm">
      {{ $t('minicart.empty') }}
      <button
        v-if="globalContent.getFastOrderEnabled && userStore.isSalesRepUser"
        class="btn btn--secondary w-full mt-16"
        type="button"
        @click="uiStore.setShowScanner(true)"
      >
        <img
          alt=""
          src="/icons/barcode-read.svg"
          class="inline-block w-16 h-16 mr-12"
        >
        {{ $t('minicart.barcode.scan') }}
      </button>
    </div>
    <RequestOrderProposalModal
      v-if="showRequestProposal"
      @close="showRequestProposal = false"
    />
  </GenericSideMenu>
</template>

<script setup lang="ts">
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import { useCartStore } from '~/store/cart';
import { storeToRefs } from 'pinia';
import { useUiStore } from '~/store/ui';
import { createEventFromCart } from '~/composables/useTracking';
import useLinkReplacer from '~/composables/useLinkReplacer';
import { useGlobalContentStore } from '~/store/globalContent';
import Tooltip from '~/components/body/Tooltip.vue';
import { useUserStore } from '~/store/user';
import LoadBasketById from '~/components/account/LoadBasketById.vue';
import CrossdockingBanner from '~/components/cart/CrossdockingBanner.vue';
import CopiedFromOrderBanner from '~/components/cart/CopiedFromOrderBanner.vue';
import CheckoutFreeShippingProgressBar from '~/components/checkout/CheckoutFreeShippingProgressBar.vue';
import EditingOrderProposalBanner from '~/components/cart/EditingOrderProposalBanner.vue';
import { NorceRelatedProductTypeCodes } from '#constants/norceCodes';
import type { CartApiItem } from '#constants/types/norce';
import useAlgoliaFetch from '~/composables/useAlgoliaFetch';
import ProductCard from '~/components/product/ProductCard.vue';
import type { ProductModel } from '#models/product';
import Product from '#models/product';
import RequestOrderProposalModal from '~/components/modals/RequestOrderProposalModal.vue';
import useLocalDate from '~/composables/useLocalDate';
import MiniCartProductV2 from '~/components/cart/MiniCartProductV2.vue';

const { getLocaleDate } = useLocalDate();
const { getExactVariantByPartNo } = useAlgoliaFetch();
const cartStore = useCartStore();
const userStore = useUserStore();
const { cartItems } = storeToRefs(cartStore);
const { $toNumber } = useNuxtApp();
const uiStore = useUiStore();
const globalContent = useGlobalContentStore();

const swiperIndex = ref(0);
const swiperH = ref(-1);
const updateSwiperH = (h: number) => {
  if (h < 500) {
    swiperH.value = h;
  }
};

const { manualUrl } = useLinkReplacer();

const route = useRoute();
const close = () => {
  uiStore.setShowMiniCart(false);
};

const emit = defineEmits(['closeMenu']);

onMounted(async() => {
  await loadRelatedProducts();
  createEventFromCart('view_cart');
});

const titleDescription = computed(() => {
  if (userStore.isSalesRepUser && cartStore.cart.norceBasketId) {
    return `#${ cartStore.cart.norceBasketId }`;
  }
  return '';
});

const clearOrderProposalForSalesRep = async() => {
  cartStore.deleteCart();
  uiStore.closeAllModal();
  emit('closeMenu');
  await userStore.setCurrentlyEditingOrderProposal(null);
};

const clearOrderProposalForCustomer = async() => {
  cartStore.deleteCart();
  uiStore.closeAllModal();
};

const hiddenPartNos = computed(() => {
  return [
    globalContent.consumerFeePartNo,
    globalContent.outsideModDeliveryFeePartNo,
    globalContent.tailLiftPartNo,
    globalContent.timedUnloadingPartNo,
  ].filter((partNo): partNo is string => Boolean(partNo));
});

const visibleCartItems = computed(() => {
  return cartStore.items.filter(item => !hiddenPartNos.value.includes(item.partNo));
});

const deliveryExtraFeeItems = computed(() => {
  const feePartNos = [
    globalContent.outsideModDeliveryFeePartNo,
    globalContent.tailLiftPartNo,
    globalContent.timedUnloadingPartNo,
  ].filter((partNo): partNo is string => Boolean(partNo));

  return cartStore.items.filter(item => feePartNos.includes(item.partNo));
});

const totalPrice = computed(() => {
  return $toNumber(cartStore.cart.summary.priceBeforeVat) - $toNumber(cartStore.cart.summary.shippingBeforeVat);
});

// On B2C, the flyout total excludes freight — freight is only broken out on the checkout page itself. B2B keeps the freight-inclusive total in the flyout.
const hideFreightInFlyoutTotal = computed(() => !isCheckout.value && globalContent.getIsB2c);

const displayTotal = computed(() => {
  return hideFreightInFlyoutTotal.value ? cartStore.cart.summary.subtotalDisplay : cartStore.cart.summary.priceDisplay;
});

const displayTotalBeforeVat = computed(() => {
  return hideFreightInFlyoutTotal.value ? cartStore.cart.summary.subtotalBeforeVatDisplay : cartStore.cart.summary.priceBeforeVatDisplay;
});

const isOrderProposalBasket = computed(() => {
  return !!(userStore.isEditingOrderProposal ? userStore.userProfile.currentlyEditingOrderProposal?.norceBasketId : null) || cartStore.isOrderProposalBasket;
});

const orderProposalId = computed(() => {
  return userStore.isEditingOrderProposal ? userStore.userProfile.currentlyEditingOrderProposal?.norceBasketId : null;
});

const copiedFromOrderId = computed(() => {
  if (userStore.isSalesRepUser || userStore.isCustomerSuccessUser) {
    return cartStore.getCopiedFromOrderId;
  }
  return null;
});

const showPoints = computed(() => {
  return userStore.allowPointsPurchase && globalContent.getPointCollectingThreshold <= totalPrice.value;
});

const isBackorderBasket = computed(() => {
  return cartStore.cart.items.some((item) => item.stockStatus.value < 1);
});

const isCheckout = computed(()=> {
  return route.name?.toString()?.endsWith('checkout');
});

const relationTypes = [
  NorceRelatedProductTypeCodes.Mouldings,
  NorceRelatedProductTypeCodes.Skirtings,
  NorceRelatedProductTypeCodes.Profiles,
  NorceRelatedProductTypeCodes.Aftercare,
  NorceRelatedProductTypeCodes.Installation,
];

const relatedProducts = ref<ProductModel[]>([]);

const cartPartNos = computed(() => {
  return cartStore.cart?.items?.map((i: CartApiItem) => i.partNo);
});
const loadRelatedProducts = async() => {
  const allPartNos = [] as string[];
  if (cartPartNos.value.length) {
    const res = await getExactVariantByPartNo(cartPartNos.value);
    if (res?.hits) {
      res.hits.map((p) => {
        p.relatedProducts?.filter(f => relationTypes.includes(f.code)).map((r) => allPartNos.push(r.partNo));
      });
    }

    const uniquePartNos = [...new Set(allPartNos)];
    if (!uniquePartNos.length) {
      return;
    }

    const products = await getExactVariantByPartNo(uniquePartNos);
    if (products) {
      relatedProducts.value = products.hits
        ?.map((p) => Product.create(p, true))
        .sort((a, b) => {
          const indexA = uniquePartNos.findIndex(pn => a.partNumbers.includes(pn));
          const indexB = uniquePartNos.findIndex(pn => b.partNumbers.includes(pn));
          return indexA - indexB;
        })
        .slice(0, 8);
    }
  }
};

const showRequestProposal = ref(false);

const showSavedCartMessage = computed(() => {
  return (userStore.isCustomerSuccessUser || userStore.isSalesRepUser) && (cartStore.cart.savedBasket && !cartStore.cart.savedBasket?.createdByMe);
});

</script>
